/*
 * CAPTURE-OWNED MODULE
 * CloudFolderPickerDialog — modal folder browser for a connected cloud drive.
 *
 * An overlay with a breadcrumb and lazily loaded children. Paths are opaque
 * Graph item ids rather than filesystem paths, and there are no ancestors to
 * walk back up (the API returns none).
 *
 * ERRORS ARE READ FROM THE BODY, NEVER FROM THE STATUS. Both "the folder you
 * clicked was deleted" and "this channel is gone" come back as 404, and they
 * demand opposite responses: the first stays in the dialog and steps back a
 * level, the second means the dialog is browsing something that no longer
 * exists and must close. Branching on the status alone would tell an admin their
 * connection had vanished because a subfolder was tidied away.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureAPI } from '../services/api';
import './CloudFolderPickerDialog.css';

/** The body string capture-service uses for "no such channel, or not yours". */
const CHANNEL_MISSING = 'Channel not found';

/**
 * WHERE THE FOLDERS COME FROM, AND WHAT CHOOSING ONE DOES.
 *
 * The dialog itself only browses. A `source` supplies the three things that
 * differ between its uses:
 *
 *   listFolders({ parent, cursor })   one page: { folders, next_cursor, drive_id? }
 *   select(folder, { driveId })       what choosing a folder does; its result is
 *                                     handed to onSelected
 *   createFolder({ parentId, name })  optional; when present the dialog offers
 *                                     "New folder"
 *   checkFolder(folder, { driveId })  optional; asked as soon as a folder is
 *                                     chosen, before anything is saved:
 *                                     { allowed } and, when refused, { error, code }
 *
 * Two exist. A SOURCE's watch folder is browsed through its channel and saved
 * on choosing (channelSource; what the dialog does when given a channelId and
 * no source). A DESTINATION's folder is browsed through the tenant's Microsoft
 * 365 connection and only handed back to the form, which saves it with the
 * rest of the destination (connectionSource).
 */
export function channelSource(channelId) {
  return {
    listFolders: (opts) => captureAPI.ingestion.listFolders(channelId, opts),
    // May this source watch it? (Not a folder a destination publishes into.)
    checkFolder: (folder) => captureAPI.ingestion.checkWatchFolder(channelId, { folderId: folder.id }),
    select: async (folder) => {
      const res = await captureAPI.ingestion.setWatchFolder(channelId, {
        folderId: folder.id,
        folderPath: folder.path,
      });
      return {
        watch_folder_id: res?.watch_folder_id ?? folder.id,
        watch_folder_path: res?.watch_folder_path ?? folder.path,
        replaced: Boolean(res?.replaced),
      };
    },
  };
}

/** A constant, so the dialog's effects see the same source on every render. */
export const connectionSource = {
  listFolders: (opts) => captureAPI.ingestion.listConnectionFolders(opts),
  createFolder: ({ parentId, name }) => captureAPI.ingestion.createConnectionFolder({ parentId, name }),
  // May a destination publish into it? (Not a folder a source watches.)
  checkFolder: (folder, { driveId }) => captureAPI.ingestion.checkConnectionFolder({ driveId, folderId: folder.id }),
  // Nothing is saved here. The drive id comes from the listing: the server
  // checks it, and the folder, again when the destination is saved.
  select: async (folder, { driveId }) => ({
    drive_id: driveId,
    folder_item_id: folder.id,
    folder_path: folder.path,
  }),
};

/** What "the top level" is handed to a source as, when a caller allows choosing it. */
const TOP_LEVEL = { id: null, name: 'All folders', path: '/' };

/** Said when the check could not be made at all. The reason is in the console. */
export const CHECK_FAILED = 'Could not check this folder. Try again, or choose another folder.';

/**
 * Browse a drive and choose a folder.
 *
 * @param {string}   channelId     a source's channel: browse and set its watch folder
 * @param {object}   source        or: where folders come from and what choosing does (see above)
 * @param {?string}  currentPath   the folder chosen already, for display only
 * @param {string}   title         the heading; defaults to the watch-folder wording
 * @param {string}   currentLabel  what precedes currentPath in the footer
 * @param {boolean}  allowRoot     may the drive's top level itself be chosen? Default NO:
 *                                 neither a watch folder nor a destination may be a whole drive
 * @param {Function} onClose       called with an optional { reason } — 'channel_missing'
 *                                 tells the page to refetch, anything else is a plain close
 * @param {Function} onSelected    called with what the source's select returned
 */
export default function CloudFolderPickerDialog({
  channelId,
  source,
  currentPath,
  title = 'Choose a folder to watch',
  currentLabel = 'Currently watching',
  allowRoot = false,
  onClose,
  onSelected,
}) {
  const src = useMemo(() => source || channelSource(channelId), [source, channelId]);
  // The drive the listing came from, for a source whose select needs it.
  const driveIdRef = useRef(null);
  // "New folder": closed, or open with the name being typed.
  const [newFolderName, setNewFolderName] = useState(null);
  const [creating, setCreating] = useState(false);

  // The navigation trail. Empty = the drive root. The API returns no ancestors,
  // so this stack IS the breadcrumb — there is nowhere else to recover it from.
  const [stack, setStack] = useState([]);
  const [folders, setFolders] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  // Ignore the answer to a request the user has already navigated away from.
  // Without this, a slow root listing can land after a fast child listing and
  // repaint the dialog with the wrong level's folders while the breadcrumb still
  // reads the new one.
  const seq = useRef(0);

  // The stack, readable synchronously. Error recovery has to know where it is
  // AFTER an await, and it must not read it inside a setState updater: React
  // StrictMode double-invokes updaters, so a load() called from one would fire
  // two requests per recovery in development.
  const stackRef = useRef([]);
  const applyStack = useCallback((next) => {
    stackRef.current = next;
    setStack(next);
    // Moving somewhere else lets go of a folder picked from the old list.
    setPicked(null);
  }, []);

  const current = stack.length ? stack[stack.length - 1] : null;

  // THE CHOSEN FOLDER, AND WHETHER IT MAY BE USED.
  //
  // Chosen is the row picked from the list, or else the folder the dialog is
  // standing in. Choosing is not confirming: the footer button confirms, and
  // only once the source has said the folder is allowed. So a folder the save
  // would refuse (one a destination publishes into, one a source watches) is
  // refused HERE, in the same words, before anything is saved or typed.
  const [picked, setPicked] = useState(null);
  const chosen = picked || current || (allowRoot ? TOP_LEVEL : null);
  // { state: 'checking' | 'allowed' | 'refused' | 'failed', message, code }
  const [check, setCheck] = useState({ state: 'allowed' });
  const [checkRound, setCheckRound] = useState(0);
  // Only the answer about the folder chosen NOW is applied. Choose A, then B
  // before A's answer arrives: A's is dropped, whatever it says.
  const checkSeq = useRef(0);
  const chosenId = chosen ? chosen.id : null;

  useEffect(() => {
    const ticket = ++checkSeq.current;
    if (!chosen || !chosen.id || !src.checkFolder) {
      setCheck({ state: 'allowed' });
      return;
    }
    setCheck({ state: 'checking' });
    (async () => {
      try {
        const res = await src.checkFolder(chosen, { driveId: driveIdRef.current });
        if (ticket !== checkSeq.current) return;
        if (res?.allowed === true) {
          setCheck({ state: 'allowed' });
        } else if (res?.allowed === false && typeof res.error === 'string' && res.error) {
          if (res.code) console.warn('[folder picker] folder refused', { code: res.code });
          setCheck({ state: 'refused', message: res.error, code: res.code || null });
        } else {
          // Neither yes nor a reasoned no: not an answer, so not a yes.
          setCheck({ state: 'failed', message: CHECK_FAILED });
        }
      } catch (err) {
        if (ticket !== checkSeq.current) return;
        console.warn('[folder picker] folder check failed', {
          status: err?.response?.status,
          code: err?.response?.data?.code,
        });
        setCheck({ state: 'failed', message: CHECK_FAILED });
      }
    })();
    // `chosen` is read for its id alone; a re-render hands a new object for the same folder.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenId, src, checkRound]);

  /**
   * The one place a folder-route failure is interpreted.
   *
   * Returns true when the caller should stop — the dialog is closing.
   */
  const handleRouteError = useCallback((err, { onFolderGone } = {}) => {
    const status = err?.response?.status;
    const data = err?.response?.data || {};

    // Only this exact body closes the dialog. An unrecognised 404 body is
    // treated as folder-level: staying open and saying so is recoverable, while
    // wrongly closing and claiming the channel is missing is not.
    if (status === 404 && data.error === CHANNEL_MISSING) {
      onClose({ reason: 'channel_missing' });
      return true;
    }

    if (status === 404) {
      setError('That folder no longer exists');
      setErrorCode(null);
      if (onFolderGone) onFolderGone();
      return false;
    }

    // The server's code is for whoever is debugging, not for the person
    // choosing a folder: it goes to the console and onto the notice as a data
    // attribute, and is not shown.
    if (data.code) console.warn('[folder picker] request refused', { status, code: data.code });

    // 502 arrives with two different strings — 'Could not reach the connected
    // drive' (drive or token) and 'Could not list folders' (the listing itself)
    // — and 400 with a third for a non-OneDrive channel type. Rendering the body
    // rather than a literal is what keeps those three distinguishable.
    setError(data.error || err?.message || 'Could not list folders');
    setErrorCode(data.code || null);
    return false;
  }, [onClose]);

  /**
   * Load one page. parentId is passed explicitly rather than read off `stack`,
   * because the setState that pushes onto the stack has not landed yet when the
   * navigation handler calls this.
   */
  const load = useCallback(async (parentId, cursor, { append = false, keepError = false } = {}) => {
    const ticket = ++seq.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    if (!keepError) {
      setError(null);
      setErrorCode(null);
    }

    try {
      const res = await src.listFolders({ parent: parentId, cursor });
      if (ticket !== seq.current) return;
      if (res?.drive_id) driveIdRef.current = res.drive_id;
      const page = Array.isArray(res?.folders) ? res.folders : [];
      setFolders((prev) => (append ? [...prev, ...page] : page));
      setNextCursor(res?.next_cursor ?? null);
    } catch (err) {
      if (ticket !== seq.current) return;
      // A failed descent leaves the stack where the user put it and shows the
      // message; the folder-gone case pops it below, in the caller.
      handleRouteError(err, {
        onFolderGone: () => {
          const prev = stackRef.current;
          // AT THE ROOT THERE IS NOWHERE TO STEP BACK TO. Reloading the same
          // level would fail the same way and recover the same way — an
          // unbounded retry loop against Graph, driven by one deleted folder.
          if (prev.length === 0) return;
          const next = prev.slice(0, -1);
          applyStack(next);
          const parentOfGone = next.length ? next[next.length - 1].id : null;
          // keepError so the pop's reload does not wipe the explanation for it.
          load(parentOfGone, null, { append: false, keepError: true });
        },
      });
      if (!append) setFolders([]);
      setNextCursor(null);
    } finally {
      if (ticket === seq.current) {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    }
  }, [src, handleRouteError, applyStack]);

  // Initial listing: the drive root, no cursor.
  useEffect(() => {
    load(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const descend = useCallback((folder) => {
    applyStack([...stackRef.current, folder]);
    load(folder.id, null);
  }, [load, applyStack]);

  const goToCrumb = useCallback((index) => {
    // index === -1 is the root crumb.
    if (index < 0) {
      applyStack([]);
      load(null, null);
      return;
    }
    applyStack(stackRef.current.slice(0, index + 1));
    load(stackRef.current[index].id, null);
  }, [load, applyStack]);

  const loadMore = useCallback(() => {
    if (!nextCursor) return;
    load(current ? current.id : null, nextCursor, { append: true });
  }, [nextCursor, current, load]);

  /** Choose a folder. What that does is the source's business. */
  const select = useCallback(async (folder) => {
    if (!folder) return;
    setSaving(true);
    setError(null);
    setErrorCode(null);
    try {
      const result = await src.select(folder, { driveId: driveIdRef.current });
      onSelected(result);
      onClose();
    } catch (err) {
      // Same vocabulary as the listing route: a 404 here is the channel, since
      // the PUT never touches Graph. onFolderGone is deliberately not passed —
      // there is no folder-level 404 on this route to step back from.
      handleRouteError(err);
    } finally {
      setSaving(false);
    }
  }, [src, onClose, onSelected, handleRouteError]);

  /**
   * Create a folder where the dialog is now, and step into it: the next thing
   * anyone does with a folder they have just made is choose it.
   *
   * A name that is already a folder's is not an error to recover from by
   * typing again. The server hands that folder back, and the dialog opens it,
   * saying so.
   */
  const createFolder = useCallback(async () => {
    const name = (newFolderName || '').trim();
    if (!name || !src.createFolder) return;
    setCreating(true);
    setError(null);
    setErrorCode(null);
    try {
      const res = await src.createFolder({ parentId: current ? current.id : null, name });
      if (res?.drive_id) driveIdRef.current = res.drive_id;
      setNewFolderName(null);
      if (res?.folder) descend(res.folder);
    } catch (err) {
      const data = err?.response?.data || {};
      if (err?.response?.status === 409 && data.code === 'folder_exists' && data.folder) {
        setNewFolderName(null);
        setError(data.error || 'A folder with that name already exists');
        setErrorCode(null);
        applyStack([...stackRef.current, data.folder]);
        load(data.folder.id, null, { keepError: true });
      } else {
        // The form stays open with the name in it, under the server's reason.
        handleRouteError(err);
      }
    } finally {
      setCreating(false);
    }
  }, [newFolderName, src, current, descend, applyStack, load, handleRouteError]);

  const busy = loading || loadingMore || saving || creating;

  return (
    <div className="cfp-overlay" onClick={() => onClose()}>
      <div className="cfp-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="cfp-header">
          <h3>{title}</h3>
          <button className="cfp-close-btn" onClick={() => onClose()} title="Close">&times;</button>
        </div>

        {/* Breadcrumb. Built entirely from the navigation stack — the folders
            route returns no ancestors, so a deep link into a subfolder is not
            something this dialog can reconstruct. */}
        <div className="cfp-breadcrumbs">
          <button className="cfp-crumb" onClick={() => goToCrumb(-1)} disabled={busy}>
            All folders
          </button>
          {stack.map((folder, i) => {
            const isLast = i === stack.length - 1;
            return (
              <React.Fragment key={folder.id}>
                <span className="cfp-crumb-sep">›</span>
                {isLast ? (
                  <span className="cfp-crumb-current">{folder.name}</span>
                ) : (
                  <button className="cfp-crumb" onClick={() => goToCrumb(i)} disabled={busy}>
                    {folder.name}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {src.createFolder && (
          <div className="cfp-new-folder">
            {newFolderName === null ? (
              <button className="cfp-new-folder-open" onClick={() => setNewFolderName('')} disabled={busy}>
                + New folder
              </button>
            ) : (
              <form
                className="cfp-new-folder-form"
                onSubmit={(e) => { e.preventDefault(); createFolder(); }}
              >
                <input
                  className="cfp-new-folder-input"
                  type="text"
                  autoFocus
                  maxLength={255}
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder={current ? `New folder in ${current.name}` : 'New folder at the top level'}
                  aria-label="New folder name"
                  disabled={creating}
                />
                <button type="submit" className="cfp-btn cfp-btn-select" disabled={busy || !newFolderName.trim()}>
                  {creating ? 'Creating…' : 'Create'}
                </button>
                <button type="button" className="cfp-btn cfp-btn-cancel" onClick={() => setNewFolderName(null)} disabled={creating}>
                  Cancel
                </button>
              </form>
            )}
          </div>
        )}

        {error && (
          <div className="cfp-error" role="alert" data-code={errorCode || undefined}>
            <span className="cfp-error-msg">{error}</span>
          </div>
        )}

        <div className="cfp-list">
          {loading ? (
            <div className="cfp-loading">Loading folders…</div>
          ) : folders.length === 0 ? (
            <div className="cfp-list-empty">No folders here.</div>
          ) : (
            folders.map((folder) => (
              <div key={folder.id} className={`cfp-row${picked?.id === folder.id ? ' cfp-row-picked' : ''}`}>
                <button
                  className="cfp-row-open"
                  onClick={() => descend(folder)}
                  disabled={busy}
                  title={folder.path}
                >
                  <span className="cfp-row-icon">&#x1F4C1;</span>
                  <span className="cfp-row-name">{folder.name}</span>
                  {typeof folder.childCount === 'number' && (
                    <span className="cfp-row-count">{folder.childCount}</span>
                  )}
                </button>
                <button
                  className="cfp-row-select"
                  onClick={() => setPicked(folder)}
                  disabled={busy}
                  aria-pressed={picked?.id === folder.id}
                >
                  {picked?.id === folder.id ? 'Selected' : 'Select'}
                </button>
              </div>
            ))
          )}

          {nextCursor && !loading && (
            <button className="cfp-load-more" onClick={loadMore} disabled={busy}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>

        {chosen && (
          <div className="cfp-chosen">
            <span className="cfp-chosen-path" title={chosen.path || ''}>
              Selected: {chosen.path || chosen.name}
            </span>
            {check.state === 'checking' && (
              <span className="cfp-check" role="status">Checking folder…</span>
            )}
            {(check.state === 'refused' || check.state === 'failed') && (
              <span
                className="cfp-check cfp-check-refused"
                role="alert"
                data-state={check.state}
                data-code={check.code || undefined}
              >
                {check.message}
                {check.state === 'failed' && (
                  <button type="button" className="cfp-check-retry" onClick={() => setCheckRound((n) => n + 1)}>
                    Try again
                  </button>
                )}
              </span>
            )}
          </div>
        )}

        <div className="cfp-footer">
          <span className="cfp-current-path" title={currentPath || ''}>
            {/* What is saved already. Silent once a folder is chosen here: the line above names it. */}
            {currentPath ? `${currentLabel} ${currentPath}` : chosen ? '' : 'No folder selected yet'}
          </span>
          <div className="cfp-footer-actions">
            <button className="cfp-btn cfp-btn-cancel" onClick={() => onClose()} disabled={saving}>
              Cancel
            </button>
            {/* Disabled at the root unless a caller allows it, and none does:
                watching an entire drive is not a choice this product offers,
                and a destination may not be a drive's top level either (the
                server refuses both). Disabled, too, until the chosen folder
                has been checked and allowed. */}
            <button
              className="cfp-btn cfp-btn-select"
              onClick={() => select(chosen)}
              disabled={busy || !chosen || check.state !== 'allowed'}
            >
              {saving ? 'Saving…' : 'Select this folder'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
